import { ApiErrorState } from '@/components/ApiErrorState/ApiErrorState'
import { BreadCrumbs } from '@/components/BreadCrumbs/BreadCrumbs'
import { DataTag } from '@/components/DataTag/DataTag'
import { ExternalLink } from '@/components/ExternalLink/ExternalLink'
import { SectionCard } from '@/components/SectionCard/SectionCard'
import { PageLayout } from '@/components/templates/PageLayout/PageLayout'
import { useOrganization } from '@/contexts/useOrganization'
import { useRBACPermissions } from '@/contexts/useRBACPermissions'
import { auditLogsRoute } from '@/features/AuditLogs/pages/AuditLogsPage/AuditLogsPage'
import { getStripeCustomerLink } from '@/features/HivesOverview/components/ExternalLinks'
import { MembersCard } from '@/features/HivesOverview/components/MembersCard'
import { FeaturesAccordion } from '@/features/Organizations/components/FeaturesAccordion'
import {
  useFindHivesByOrgId,
  useInternalApiKey,
  useOneMoreTrial,
} from '@/generated/http-clients/v2ApiComponents'
import { toTokenData } from '@/helpers/toTokenData'
import { toaster } from '@/hooks/useToast/toastUtils'
import { hivesOverviewRootRoute } from '@/router/rootRoutes'
import {
  Button,
  Grid,
  GridItem,
  HStack,
  Icon,
  Skeleton,
  Table,
  Text,
  VStack,
} from '@chakra-ui/react'
import type { UseQueryResult } from '@tanstack/react-query'
import { Link, createRoute, useNavigate } from '@tanstack/react-router'
import { FileText, Plus } from 'lucide-react'
import { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { getColorSchemeForStatus } from '../util'
import { hivesOverviewClusterDetailsRoute } from './HivesOverviewClusterDetails'

export const hivesOverviewOrganizationDetailsRoute = createRoute({
  getParentRoute: () => hivesOverviewRootRoute,
  path: '/orgs/$orgId',
  component: HivesOverviewsOrganizationDetailsPage,
})

export function HivesOverviewsOrganizationDetailsPage() {
  const params = hivesOverviewOrganizationDetailsRoute.useParams()
  const navigate = useNavigate()

  const { t: tCommon } = useTranslation(undefined, {
    useSuspense: false,
  })
  const { t } = useTranslation('features', {
    useSuspense: false,
  })
  const orgContext = useOrganization()

  const { hasAdminRights, hasOperatorRights } = useRBACPermissions()

  const isAdmin = hasAdminRights()
  const isOperator = hasOperatorRights()

  const canAddTrial = isAdmin || isOperator
  const canViewInternalApiToken = isAdmin || isOperator
  const canManageSupportUsers = isAdmin || isOperator

  const orgId = useMemo(() => {
    return params.orgId
  }, [params.orgId])

  useEffect(() => {
    orgContext.setSelectedOrganizationId(orgId)
  }, [orgContext.setSelectedOrganizationId, orgId])

  const hivesRequest = useFindHivesByOrgId({
    pathParams: {
      orgId: orgId,
    },
  })

  // TODO Oli: if/when console-backoffice-openapi-v2.yml gets updated and
  // users_info would be a required array with at least 1 element in
  // OrganizationHivesResponse, then we might be able to simplify this code
  function firstUserId() {
    const users = hivesRequest.data?.users_info || []
    if (users.length === 0) {
      return 'unknown_customer_id'
    }

    return btoa(users[0].user_id)
  }

  const internalApiKey = useInternalApiKey(
    {
      pathParams: {
        orgId: orgId,
      },
      queryParams: {
        // FIXME @RobinAtherton - Check out if this is correct but this is how the old implementation grabbed the customerId
        customerId: firstUserId(),
      },
    },
    {
      enabled: canViewInternalApiToken,
    },
  )

  const oneMoreTrial = useOneMoreTrial({
    onSuccess: () => {
      toaster.create({
        title: t('orgDetails.addTrial.success'),
        type: 'success',
      })
    },
    onError: () => {
      toaster.create({
        title: t('orgDetails.addTrial.error'),
        type: 'error',
      })
    },
  })

  const handleAddTrial = () => {
    oneMoreTrial.mutate({
      pathParams: {
        orgId: orgId,
      },
    })
  }

  const decodedToken = useMemo(() => {
    if (internalApiKey.data?.token) {
      return toTokenData(internalApiKey.data?.token)
    }
    return null
  }, [internalApiKey.data?.token])

  const breadCrumbs = [
    { name: tCommon('sidebar.runningHives'), path: hivesOverviewRootRoute.to },
    { name: orgId, path: '' },
  ]

  function routeToClusterDetails(orgId: string, clusterId: string | undefined) {
    if (clusterId) {
      navigate({
        to: hivesOverviewClusterDetailsRoute.to,
        params: {
          orgId,
          clusterId,
        },
      })
    } else {
      toaster.create({
        title: t('toast.clusterNotAccessible'),
        type: 'warning',
      })
    }
  }

  return (
    <PageLayout
      title={t('orgDetails.pageTitle')}
      beforeTitle={<BreadCrumbs paths={breadCrumbs} />}
      action={
        <Link to={auditLogsRoute.to} search={{ dateRange: 'last24Hours' }}>
          <Button variant="outline" colorPalette="gray" size="sm">
            <Icon as={FileText} />
            {String(t('auditLogs.contextualAccess.viewAuditLogs'))}
          </Button>
        </Link>
      }
    >
      {hivesRequest.isLoading ? <Skeleton h="500px" w="100%" /> : null}
      {hivesRequest.isError || !hivesRequest.data ? (
        <ApiErrorState
          apiResource={hivesRequest as unknown as UseQueryResult}
          errorTitle={t('orgDetails.error.title')}
          errorDescription={t('orgDetails.error.description')}
        />
      ) : null}
      {hivesRequest.isSuccess ? (
        <>
          <SectionCard key="Features" title={t('orgDetails.features.title')}>
            <FeaturesAccordion />
          </SectionCard>

          <Grid templateColumns="repeat(2, 1fr)" width="100%" gap={4}>
            <GridItem>
              <SectionCard title={t('orgDetails.details.title')}>
                <VStack alignItems="start" gap={6}>
                  <VStack alignItems="start">
                    <HStack justifyItems="center">
                      <Text>{t('orgDetails.details.orgId')}:</Text>
                      <DataTag
                        value={hivesRequest.data?.org_id}
                        fontWeight={500}
                        fontSize="1rem"
                        height="1.5rem"
                      >
                        {hivesRequest.data?.org_id}
                      </DataTag>
                    </HStack>
                    <HStack justifyItems="center">
                      <Text>{t('orgDetails.details.orgName')}:</Text>
                      <DataTag
                        value={hivesRequest.data?.company_name}
                        fontWeight={500}
                        fontSize="1rem"
                        height="1.5rem"
                      >
                        {hivesRequest.data?.company_name}
                      </DataTag>
                    </HStack>
                    <HStack justifyItems="center">
                      <Text>{t('orgDetails.details.stripeId')}:</Text>
                      <DataTag
                        value={hivesRequest.data?.stripe_id}
                        fontWeight={500}
                        fontSize="1rem"
                        height="1.5rem"
                        externalLink={
                          hivesRequest.data?.stripe_id
                            ? getStripeCustomerLink(hivesRequest.data.stripe_id)
                            : undefined
                        }
                        colorPalette="purple"
                      >
                        {hivesRequest.data?.stripe_id}
                      </DataTag>
                    </HStack>
                  </VStack>
                  {canAddTrial && (
                    <VStack alignItems="start">
                      <Button
                        colorPalette="brand"
                        variant="solid"
                        onClick={handleAddTrial}
                        loading={oneMoreTrial.isPending}
                        disabled={oneMoreTrial.isPending}
                      >
                        <Icon as={Plus} />
                        {t('orgDetails.addTrial.button')}
                      </Button>
                      <Text fontWeight={400} fontSize="sm" color="gray">
                        {t('orgDetails.addTrial.description')}
                      </Text>
                    </VStack>
                  )}
                </VStack>
              </SectionCard>
            </GridItem>
            {canViewInternalApiToken && (
              <GridItem>
                <SectionCard
                  title={t('orgDetails.internalApiToken.title')}
                  width="100%"
                  height="100%"
                >
                  <VStack alignItems="start">
                    <HStack>
                      <Text whiteSpace="nowrap">
                        {t('orgDetails.internalApiToken.token')}:
                      </Text>
                      <DataTag
                        value={decodedToken?.Token}
                        maxWidthPx={200}
                        fontWeight={500}
                        fontSize="1rem"
                      >
                        {decodedToken?.Token}
                      </DataTag>
                    </HStack>
                    <HStack>
                      <Text>
                        {t('orgDetails.internalApiToken.expiryDate')}:
                      </Text>
                      <DataTag
                        value={decodedToken?.Expiry}
                        fontWeight={500}
                        fontSize="1rem"
                      >
                        {decodedToken?.Expiry}
                      </DataTag>
                    </HStack>
                  </VStack>
                </SectionCard>
              </GridItem>
            )}
          </Grid>
          <MembersCard
            orgId={orgId}
            canManageSupportUsers={canManageSupportUsers}
          />

          {hivesRequest.data?.hives_info?.map((hive) => (
            <SectionCard
              key={hive.cluster_id}
              title={t('orgDetails.hiveData.title')}
            >
              <Table.Root>
                <Table.Body>
                  <Table.Row>
                    <Table.Cell>
                      {t('orgDetails.hiveData.clusterId')}
                    </Table.Cell>
                    <Table.Cell>
                      <DataTag
                        value={hive.cluster_id}
                        fontWeight={500}
                        fontSize="1rem"
                        onClickBody={() =>
                          routeToClusterDetails(orgId, hive.cluster_id)
                        }
                      >
                        {hive.cluster_id}
                      </DataTag>
                    </Table.Cell>
                  </Table.Row>
                  <Table.Row>
                    <Table.Cell>
                      {t('orgDetails.hiveData.clusterType')}
                    </Table.Cell>
                    <Table.Cell>
                      <DataTag
                        value={hive.plan}
                        fontWeight={500}
                        fontSize="1rem"
                      >
                        {hive.plan}
                      </DataTag>
                    </Table.Cell>
                  </Table.Row>
                  <Table.Row>
                    <Table.Cell>{t('orgDetails.hiveData.status')}</Table.Cell>
                    <Table.Cell>
                      <DataTag
                        value={hive.hive_status}
                        fontWeight={500}
                        fontSize="1rem"
                        hasCopy={false}
                        colorPalette={getColorSchemeForStatus(hive.hive_status)}
                      >
                        {hive.hive_status}
                      </DataTag>
                    </Table.Cell>
                  </Table.Row>
                  <Table.Row>
                    <Table.Cell>{t('orgDetails.hiveData.apiary')}</Table.Cell>
                    <Table.Cell>
                      <DataTag
                        value={hive.apiary}
                        fontWeight={500}
                        fontSize="1rem"
                      >
                        {hive.apiary}
                      </DataTag>
                    </Table.Cell>
                  </Table.Row>
                  <Table.Row>
                    <Table.Cell>
                      {t('orgDetails.hiveData.clusterUrl')}
                    </Table.Cell>
                    <Table.Cell>
                      <DataTag
                        value={hive.cluster_url}
                        fontWeight={500}
                        fontSize="1rem"
                      >
                        {hive.cluster_url}
                      </DataTag>
                    </Table.Cell>
                  </Table.Row>
                  <Table.Row>
                    <Table.Cell>
                      {t('orgDetails.hiveData.authMethod')}
                    </Table.Cell>
                    <Table.Cell>
                      <DataTag
                        value={hive.authentication_method}
                        fontWeight={500}
                        fontSize="1rem"
                        hasCopy={false}
                      >
                        {hive.authentication_method}
                      </DataTag>
                    </Table.Cell>
                  </Table.Row>
                  <Table.Row>
                    <Table.Cell>{t('orgDetails.hiveData.features')}</Table.Cell>
                    <Table.Cell>
                      {hive.broker_features.map((feature) => (
                        <DataTag
                          colorPalette="orange"
                          value={feature}
                          key={feature}
                          hasCopy={false}
                        >
                          {feature}
                        </DataTag>
                      ))}
                    </Table.Cell>
                  </Table.Row>
                  <Table.Row>
                    <Table.Cell>{t('orgDetails.hiveData.links')}</Table.Cell>
                    <Table.Cell>
                      <HStack gap={4}>
                        {Object.entries(hive.links ?? {}).map(
                          ([linkName, url]) => (
                            <ExternalLink key={url} href={url}>
                              {linkName}
                            </ExternalLink>
                          ),
                        )}
                      </HStack>
                    </Table.Cell>
                  </Table.Row>
                </Table.Body>
              </Table.Root>
            </SectionCard>
          ))}
        </>
      ) : null}
    </PageLayout>
  )
}
